/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { BotMode } from '@coze-arch/bot-api/developer_api';
import { BotPageFromEnum } from '@coze-arch/bot-typings/common';
import { type BotInfoForUpdate } from '@coze-arch/idl/playground_api';

import { usePageRuntimeStore } from '@/store/page-runtime';
import { useInferenceModeStore } from '@/store/inference-mode';
import { useDocumentWorkspaceStore } from '@/store/document-workspace';
import { useModelStore } from '@/store/model';
import { useBotInfoStore } from '@/store/bot-info';
import {
  saveFetcher,
  updateBotRequest,
} from '@/save-manager/utils/save-fetcher';
import { ItemTypeExtra } from '@/save-manager/types';

import { botSkillSaveManager } from './bot-skill';
import { modelSaveManager } from './model';
import { modelInfoWithCompatibleOntologyApp } from './ontology-app-persistence';
import { personaSaveManager } from './persona';

const managers = [personaSaveManager, botSkillSaveManager, modelSaveManager];

/** 串行化 flush，避免快速连点时更早的保存覆盖更新的模式。 */
let flushGeneration = 0;
let flushChain: Promise<void> = Promise.resolve();

const isInferenceConfigReadonly = () => {
  const { editable, isPreview, pageFrom, init } =
    usePageRuntimeStore.getState();
  return (
    !init || !editable || isPreview || pageFrom === BotPageFromEnum.Explore
  );
};

export const autosaveManager = {
  start: () => {
    console.log('start:>>');
    managers.forEach(manager => {
      manager.start();
    });
  },
  close: () => {
    console.log('close:>>');
    managers.forEach(manager => {
      manager.close();
    });
  },
  /**
   * 立即保存 inference_mode + document_workspace，并等待完成。
   * - 串行队列 + generation：快速连点时只落最新快照，避免旧请求后写覆盖
   * - 走 updateBotRequest（会抛错），不经 saveFetcher 吞错
   * - 只读 / 未 init / MultiMode 时跳过（草稿不可改或无需写）
   */
  flushInferenceConfigAsync: (): Promise<void> => {
    const gen = ++flushGeneration;
    const run = flushChain.then(async () => {
      if (gen !== flushGeneration) {
        return;
      }
      if (isInferenceConfigReadonly()) {
        return;
      }
      const { mode } = useBotInfoStore.getState();
      if (mode === BotMode.MultiMode) {
        return;
      }

      // 排队等到自己时再取最新 store，保证写入与当前 UI 一致
      const inferenceMode = useInferenceModeStore.getState();
      const documentWorkspace = useDocumentWorkspaceStore.getState();
      const modelStore = useModelStore.getState();
      if (gen !== flushGeneration) {
        return;
      }

      // inference_mode 尚未进 IDL BotInfoForUpdate，与 autosave 路径一致
      const nextMode = inferenceMode.transformVo2Dto(inferenceMode);
      const modelInfo = await modelInfoWithCompatibleOntologyApp(
        nextMode,
        modelStore.transformVo2Dto(modelStore.config),
      );
      if (gen !== flushGeneration) {
        return;
      }

      const payload = {
        inference_mode: nextMode,
        document_workspace:
          documentWorkspace.transformVo2Dto(documentWorkspace),
        model_info: modelInfo,
      } as BotInfoForUpdate & { inference_mode?: string };

      await saveFetcher(
        () => updateBotRequest(payload),
        ItemTypeExtra.InferenceMode,
        { rethrow: true },
      );
    });

    // 保持链不断；调用方仍 await 自己这一代的结果（含失败）
    flushChain = run.catch(() => undefined);
    return run;
  },
};
